import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiActivity, FiAlertOctagon, FiAlertTriangle, FiBellOff, FiCheck, FiChevronDown, FiChevronRight, FiClock,
  FiExternalLink, FiMoreHorizontal, FiPower, FiSearch, FiThermometer, FiUser, FiX, FiZapOff,
} from "react-icons/fi";

import AlertRules from "@/components/AlertRules";
import { api, getOverview, OVERVIEW_REFRESH_MS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSignalBands } from "@/lib/bands";
import { fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import {
  buildIncidents, correlate, groupAcked, isDown, kindOf, sortIncidents, STALE_MS,
  type Group, type Incident, type Kind, type Sev,
} from "@/lib/incidents";
import type { Alert, ONTHistoryResponse } from "@/lib/types";
import { useNow } from "@/components/MonitoringBanner";
import { durText } from "@/components/OntStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type T = (k: Key, p?: Record<string, string | number>) => string;
type AlertsResponse = { alerts: Alert[]; count: number };
type View = "active" | "resolved" | "rules";
type Quick = "attention" | "critical" | "offline" | "optical" | "grouped" | "all";
type GroupBy = "incident" | "none" | "olt" | "pon" | "type";

// ---------- vocabulary ----------

const KIND_ICON: Record<Kind, IconType> = {
  power: FiPower, los: FiZapOff, offline: FiAlertOctagon, optical: FiActivity, oltRx: FiActivity,
  temperature: FiThermometer, supply: FiPower, other: FiAlertTriangle,
};

/** "Weak optical signal" / "Optical signal critical": the title depends on severity for telemetry. */
function title(kind: Kind, sev: Sev, t: T) {
  if (kind === "optical" || kind === "oltRx") return t(`al.kind.${kind}.${sev === "critical" ? "crit" : "warn"}` as Key);
  return t(`al.kind.${kind}` as Key);
}

const UNIT: Record<string, string> = {
  "ont.rx_power_dbm": "dBm", "ont.olt_rx_dbm": "dBm", "ont.tx_power_dbm": "dBm",
  "ont.temperature_c": "°C", "ont.voltage_v": "V", "ont.current_ma": "mA",
};
const fmtVal = (a: Pick<Alert, "value" | "metric">) =>
  a.value === null || a.value === undefined ? "—" : `${a.value.toFixed(2)} ${UNIT[a.metric] ?? ""}`.trim();

function thresholdText(a: Alert, t: T) {
  if (a.threshold === null || a.threshold === undefined) return "";
  const v = `${a.threshold.toFixed(2)} ${UNIT[a.metric] ?? ""}`.trim();
  const below = a.condition === "<" || a.condition === "<=";
  return t(below ? "al.below" : "al.above", { sev: t(`al.sev.${a.severity === "critical" ? "critical" : "warn"}` as Key), v });
}

function SevTag({ s, big }: { s: Sev; big?: boolean }) {
  const { t } = useI18n();
  return (
    <span className={cn("inline-flex w-[3.4rem] justify-center rounded-sm font-semibold tracking-[0.1em] uppercase",
      big ? "px-2 py-0.5 text-[11px]" : "py-px text-[10px]",
      s === "critical" ? "bg-signal-crit text-white" : s === "warn" ? "bg-amber-400/15 text-signal-warn" : "bg-slate-700/60 text-slate-300")}>
      {t(`al.sevShort.${s}` as Key)}
    </span>
  );
}

function StateTag({ i }: { i: Pick<Incident, "acked" | "suppressed" | "stale" | "ackedBy"> }) {
  const { t } = useI18n();
  if (i.suppressed) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-[0.1em] text-slate-400 uppercase"><FiBellOff className="size-3" />{t("al.state.muted")}</span>;
  if (i.acked) return <span className="text-[11px] font-semibold tracking-[0.1em] text-slate-400 uppercase" title={i.ackedBy}><FiCheck className="mr-1 inline size-3" />{t("al.state.ack")}</span>;
  return <span className="text-[11px] font-semibold tracking-[0.1em] text-signal uppercase">{t("al.state.unack")}</span>;
}

// ---------- page ----------

export default function AlertsPage() {
  const { t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const view = (["active", "resolved", "rules"].includes(sp.get("view") ?? "") ? sp.get("view") : "active") as View;
  const set = (k: string, v: string | null) => { const p = new URLSearchParams(sp); if (v === null) p.delete(k); else p.set(k, v); setSp(p, { replace: true }); };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("nav.alerts")}</h1>
        <div role="tablist" className="ml-4 flex rounded-sm border border-rule p-0.5">
          {(["active", "resolved", "rules"] as View[]).map((v) => (
            <button key={v} role="tab" type="button" aria-selected={view === v} onClick={() => set("view", v === "active" ? null : v)}
              className={cn("rounded-sm px-3 py-1 text-[12.5px]", view === v ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
              {t(`al.view.${v}` as Key)}
            </button>
          ))}
        </div>
      </div>
      {view === "active" && <ActiveConsole />}
      {view === "resolved" && <ResolvedView />}
      {view === "rules" && <AlertRules />}
    </div>
  );
}

function useAlertActions() {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ["alerts"] });
    qc.invalidateQueries({ queryKey: ["alert-events"] });
    qc.invalidateQueries({ queryKey: ["overview"] });
  };
  const bulk = useMutation({
    mutationFn: (b: { ids: number[]; action: "ack" | "assign" | "suppress"; note?: string; user_id?: number | null; minutes?: number }) =>
      api<{ done: number; skipped: number }>("/api/v1/alerts/bulk", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: done,
  });
  const close = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => api(`/api/v1/alerts/${id}/close`, { method: "POST" }))),
    onSuccess: done,
  });
  return { bulk, close };
}

function ActiveConsole() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const canAct = user?.role === "admin" || user?.role === "noc";
  const [sp, setSp] = useSearchParams();
  const quick = (sp.get("q") ?? "attention") as Quick;
  const groupBy = (sp.get("g") ?? "incident") as GroupBy;
  const set = (k: string, v: string | null) => { const p = new URLSearchParams(sp); if (v === null) p.delete(k); else p.set(k, v); setSp(p, { replace: true }); };
  const [needle, setNeedle] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  // ?open=ont:<id> deep-links one incident (from a subscriber or ONT page).
  const [openKey, setOpenKey] = useState<string | null>(() => sp.get("open"));
  const [ackFor, setAckFor] = useState<Incident[] | null>(null);
  const now = useNow(15_000);
  const q = useQuery({ queryKey: ["alerts", "active"], queryFn: () => api<AlertsResponse>("/api/v1/alerts?view=active&limit=1000"), refetchInterval: 30_000 });
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const { bulk } = useAlertActions();

  const alerts = q.data?.alerts ?? [];
  const incidents = useMemo(() => buildIncidents(alerts, now), [alerts, now]);
  const ponTotals = useMemo(() => new Map((ov.data?.pons ?? []).map((p) => [p.id, p.onts_total])), [ov.data]);
  const { groups, rest } = useMemo(() => correlate(incidents, ponTotals), [incidents, ponTotals]);

  const match = (i: Incident) => {
    const n = needle.trim().toLowerCase();
    if (n && ![i.target.label, i.target.place, i.target.ponPath, i.target.oltName, i.primary.serial, `#${i.target.ontNum}`, t(`al.kind.${i.kind === "optical" || i.kind === "oltRx" ? `${i.kind}.warn` : i.kind}` as Key)]
      .some((v) => v?.toLowerCase().includes(n))) return false;
    switch (quick) {
      case "attention": return !i.acked && !i.suppressed;
      case "critical": return i.severity === "critical";
      case "offline": return isDown(i.kind);
      case "optical": return i.kind === "optical" || i.kind === "oltRx";
      case "grouped": return !!i.groupKey;
      default: return true;
    }
  };
  const groupsShown = groups.filter((g) => g.children.some(match) && (quick !== "attention" || !groupAcked(g)));
  const singles = sortIncidents(rest.filter(match));
  const all = [...groups.flatMap((g) => g.children), ...rest];

  // Summary: counts over everything active, not over the filter.
  const n = (v: number) => fmtNum(locale, v);
  const crit = all.filter((i) => i.severity === "critical").length;
  const warn = all.filter((i) => i.severity === "warn").length;
  const unacked = all.filter((i) => !i.acked && !i.suppressed).length;
  const down = all.filter((i) => isDown(i.kind)).length;
  const optical = all.filter((i) => i.kind === "optical" || i.kind === "oltRx").length;

  const selected = all.filter((i) => sel.has(i.key));
  const selIds = selected.flatMap((i) => i.alerts.filter((a) => a.status !== "closed").map((a) => a.id));
  const toggle = (keys: string[], on: boolean) => { const s = new Set(sel); keys.forEach((k) => (on ? s.add(k) : s.delete(k))); setSel(s); };
  const openIncident = all.find((i) => i.key === openKey) ?? null;

  const quicks: { q: Quick; n: number }[] = [
    { q: "attention", n: unacked }, { q: "critical", n: crit }, { q: "offline", n: down }, { q: "optical", n: optical },
    { q: "grouped", n: groups.reduce((a, g) => a + g.children.length, 0) }, { q: "all", n: all.length },
  ];

  return (
    <div className="space-y-2">
      <p className="data text-[12.5px] text-slate-400">
        <span className="text-slate-100">{t("al.sum.incidents", { n: n(all.length) })}</span>
        <span className="text-slate-600"> ({t("al.sum.alerts", { n: n(alerts.length) })})</span>
        {"  ·  "}<span className={crit ? "text-signal-crit" : ""}>{t("al.sum.crit", { n: n(crit) })}</span>
        {"  ·  "}<span className={warn ? "text-signal-warn" : ""}>{t("al.sum.warn", { n: n(warn) })}</span>
        {"  ·  "}<span className={unacked ? "text-signal" : ""}>{t("al.sum.attention", { n: n(unacked) })}</span>
        {"  ·  "}{t("al.sum.down", { n: n(down) })}{"  ·  "}{t("al.sum.optical", { n: n(optical) })}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {quicks.map((x) => (
          <button key={x.q} type="button" aria-pressed={quick === x.q} onClick={() => set("q", x.q === "attention" ? null : x.q)}
            className={cn("rounded-sm border px-2 py-1 text-[12px]", quick === x.q ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
            {t(`al.quick.${x.q}` as Key)} <span className="data text-slate-500">{n(x.n)}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full max-w-72">
          <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input value={needle} onChange={(e) => setNeedle(e.target.value)} placeholder={t("al.search")} aria-label={t("al.search")} className="h-8 border-rule bg-panel pl-8 text-[12.5px]" />
        </div>
        <label className="flex items-center gap-1.5 text-[12px] text-slate-400">
          {t("al.groupBy")}
          <select value={groupBy} onChange={(e) => set("g", e.target.value === "incident" ? null : e.target.value)}
            className="h-8 rounded-md border border-rule bg-panel px-2 text-[12.5px] text-slate-200">
            {(["incident", "none", "olt", "pon", "type"] as GroupBy[]).map((g) => <option key={g} value={g}>{t(`al.group.${g}` as Key)}</option>)}
          </select>
        </label>
      </div>

      {canAct && selected.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-signal/40 bg-signal/[0.07] px-3 py-1.5 text-[12.5px]">
          <span className="text-slate-100">{t("al.bulk.n", { n: selected.length, alerts: selIds.length })}</span>
          <Button size="xs" onClick={() => setAckFor(selected)}><FiCheck /> {t("al.act.ack")}</Button>
          <Button size="xs" variant="outline" disabled={bulk.isPending} onClick={() => bulk.mutate({ ids: selIds, action: "assign", user_id: user!.id })}><FiUser /> {t("al.act.assignMe")}</Button>
          <Button size="xs" variant="outline" disabled={bulk.isPending} onClick={() => bulk.mutate({ ids: selIds, action: "suppress", minutes: 60 })}><FiBellOff /> {t("al.act.mute1h")}</Button>
          <button type="button" onClick={() => setSel(new Set())} className="ml-auto text-slate-400 hover:text-slate-100">{t("al.bulk.clear")}</button>
        </div>
      )}

      {q.isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
      {q.error && <p className="text-signal-crit">{(q.error as Error).message}</p>}

      {q.data && (
        <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
          <table className="w-full min-w-[1040px] table-fixed text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                <th className="w-9 px-3">{canAct && <input type="checkbox" aria-label={t("al.selectAll")} className="accent-cyan-400"
                  checked={singles.length > 0 && singles.every((i) => sel.has(i.key))} onChange={(e) => toggle(singles.map((i) => i.key), e.target.checked)} />}</th>
                {(["al.col.sev", "al.col.incident", "al.col.target", "al.col.current", "al.col.age", "al.col.state", "al.col.owner"] as Key[]).map((k, i) => (
                  <th key={k} className={cn("eyebrow px-3 py-2 font-medium", ["w-[5.2rem]", "w-[22%]", "w-[27%]", "w-[15%]", "w-[7%]", "w-[8%]", ""][i])}>{t(k)}</th>
                ))}
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {groupBy === "incident" && groupsShown.map((g) => (
                <GroupRows key={g.key} g={g} match={match} sel={sel} toggle={toggle} now={now} canAct={canAct} onOpen={setOpenKey} onAck={setAckFor} />
              ))}
              {groupBy === "none"
                ? <RawRows alerts={alerts} incidents={all} match={match} now={now} onOpen={setOpenKey} />
                : groupBy === "incident"
                  ? singles.map((i) => <IncidentRow key={i.key} i={i} sel={sel} toggle={toggle} now={now} canAct={canAct} onOpen={setOpenKey} onAck={setAckFor} />)
                  : <SectionRows by={groupBy} items={sortIncidents(all.filter(match))} sel={sel} toggle={toggle} now={now} canAct={canAct} onOpen={setOpenKey} onAck={setAckFor} />}
              {groupsShown.length === 0 && singles.length === 0 && groupBy === "incident" && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">
                  {quick === "attention" ? <><FiCheck className="mr-1.5 inline text-signal-good" />{t("al.empty.attention", { n: all.length })}</> : t("al.empty.filter")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {openIncident && <IncidentDrawer i={openIncident} group={groups.find((g) => g.key === openIncident.groupKey) ?? null} now={now} canAct={canAct} onClose={() => setOpenKey(null)} onAck={setAckFor} />}
      {ackFor && <AckDialog incidents={ackFor} onClose={() => { setAckFor(null); setSel(new Set()); }} />}
    </div>
  );
}

// ---------- rows ----------

type RowProps = { sel: Set<string>; toggle: (k: string[], on: boolean) => void; now: number; canAct: boolean; onOpen: (k: string) => void; onAck: (i: Incident[]) => void };

function IncidentRow({ i, sel, toggle, now, canAct, onOpen, onAck, child }: RowProps & { i: Incident; child?: boolean }) {
  const { t, locale } = useI18n();
  const Icon = KIND_ICON[i.kind];
  const a = i.primary;
  const extra = i.alerts.length - 1;
  return (
    <tr onClick={() => onOpen(i.key)}
      className={cn("group cursor-pointer border-b border-rule/50 last:border-b-0 hover:bg-white/[0.025]",
        i.acked || i.suppressed ? "opacity-70" : "", child && "bg-white/[0.012]")}>
      <td className="px-3" onClick={(e) => e.stopPropagation()}>
        {canAct && <input type="checkbox" aria-label={t("al.select")} className="accent-cyan-400" checked={sel.has(i.key)} onChange={(e) => toggle([i.key], e.target.checked)} />}
      </td>
      <td className="px-3 py-2"><SevTag s={i.severity} /></td>
      <td className={cn("px-3 py-2", child && "pl-6")}>
        <div className="flex items-center gap-1.5 font-medium text-slate-100">
          <Icon className={cn("size-3.5 shrink-0", i.kind === "power" ? "text-amber-300" : i.severity === "critical" ? "text-signal-crit" : "text-signal-warn")} />
          <span className="truncate">{title(i.kind, i.severity, t)}</span>
        </div>
        <div className="truncate text-[11.5px] text-slate-500">
          {t(`al.sub.${i.kind}` as Key)}
          {extra > 0 && <span className="text-slate-400"> · {t(extra === 1 ? "al.related1" : "al.related", { n: extra })}</span>}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="truncate text-slate-200 group-hover:text-signal" title={i.target.label}>{i.target.label || "—"}</div>
        <div className="data truncate text-[11.5px] text-slate-500">{i.target.ontNum ? `ONT #${i.target.ontNum}` : ""}{i.target.ponPath ? ` · PON ${i.target.ponPath}` : ""}{i.target.oltName ? ` · ${i.target.oltName}` : ""}</div>
      </td>
      <td className="px-3 py-2">
        {isDown(i.kind) ? (
          <span className="text-[12.5px] text-slate-300">{t(a.run_state === "Online" ? "al.cur.backOnline" : "al.cur.offline")}</span>
        ) : i.stale ? (
          <><div className="data text-[12.5px] text-slate-400 line-through decoration-slate-600">{fmtVal(a)}</div><div className="text-[11px] text-amber-200">{t("al.cur.stale", { age: durText(now - Date.parse(a.value_at!)) })}</div></>
        ) : (
          <><div className={cn("data text-[13px]", i.severity === "critical" ? "text-signal-crit" : "text-signal-warn")}>{fmtVal(a)}</div>
            <div className="truncate text-[11px] text-slate-500">{thresholdText(a, t)}</div></>
        )}
      </td>
      <td className="data px-3 py-2 text-slate-300" title={t("al.openedAt", { at: fmtDateTime(locale, i.openedAt) })}>{durText(now - Date.parse(i.openedAt))}</td>
      <td className="px-3 py-2"><StateTag i={i} /></td>
      <td className="truncate px-3 py-2 text-[12px] text-slate-400">{i.owner ? i.owner.split("@")[0] : <span className="text-slate-600">—</span>}</td>
      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {canAct && !i.acked && <Button size="xs" variant="outline" onClick={() => onAck([i])}>{t("al.act.ackShort")}</Button>}
          <RowMenu i={i} canAct={canAct} onOpen={() => onOpen(i.key)} />
        </div>
      </td>
    </tr>
  );
}

function GroupRows({ g, match, ...p }: RowProps & { g: Group; match: (i: Incident) => boolean }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const kids = sortIncidents(g.children.filter(match));
  const names = g.children.map((c) => c.target.label).filter(Boolean);
  const Icon = g.kind === "powerOutage" ? FiPower : FiZapOff;
  const acked = groupAcked(g);
  return (
    <>
      <tr className={cn("border-b border-rule/60", g.severity === "critical" ? "bg-signal-crit/[0.07]" : "bg-amber-400/[0.05]")}>
        <td className="px-3" onClick={(e) => e.stopPropagation()}>
          {p.canAct && <input type="checkbox" aria-label={t("al.select")} className="accent-cyan-400" checked={g.children.every((c) => p.sel.has(c.key))} onChange={(e) => p.toggle(g.children.map((c) => c.key), e.target.checked)} />}
        </td>
        <td className="px-3 py-2.5"><SevTag s={g.severity} /></td>
        <td className="px-3 py-2.5" colSpan={2}>
          <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start gap-2 text-left">
            <span className="mt-0.5 text-slate-400">{open ? <FiChevronDown /> : <FiChevronRight />}</span>
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-[13.5px] font-semibold tracking-[0.04em] text-slate-100 uppercase">
                <Icon className={g.kind === "powerOutage" ? "text-amber-300" : "text-signal-crit"} />
                {t(`al.grp.${g.kind}` as Key, { pon: g.ponPath, olt: g.oltName })}
              </span>
              <span className="mt-0.5 block text-[12px] text-slate-300">
                {t(`al.grp.${g.kind}.what` as Key, { n: g.children.length, spread: durText(g.spreadMs || 1000), total: g.total ?? "?" })}
                <span className="text-slate-500"> — {t(`al.grp.${g.kind}.hint` as Key)}</span>
              </span>
              <span className="mt-0.5 block truncate text-[11.5px] text-slate-500">{names.slice(0, 4).join(" · ")}{names.length > 4 ? ` · ${t("al.more", { n: names.length - 4 })}` : ""}</span>
            </span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-[12px] text-slate-300">{g.kind === "ponOutage" && g.total ? t("al.grp.affected", { n: g.children.length, total: g.total }) : t("al.grp.onts", { n: g.children.length })}</td>
        <td className="data px-3 py-2.5 text-slate-300" title={fmtDateTime(locale, g.firstAt)}>{durText(p.now - Date.parse(g.firstAt))}</td>
        <td className="px-3 py-2.5"><StateTag i={{ acked, suppressed: false, stale: false, ackedBy: "" }} /></td>
        <td />
        <td className="px-3 py-2.5 text-right">
          <div className="flex justify-end gap-1">
            {p.canAct && !acked && <Button size="xs" variant="outline" onClick={() => p.onAck(g.children)}>{t("al.act.ackAll")}</Button>}
            {g.ponId !== null && g.oltId !== null && <Link to={`/olts/${g.oltId}?pon=${encodeURIComponent(g.ponPath)}`} className="inline-flex h-6 items-center rounded-md border border-rule px-2 text-xs whitespace-nowrap text-slate-200 hover:border-signal/50">{t("al.act.pon")}</Link>}
          </div>
        </td>
      </tr>
      {open && kids.map((i) => <IncidentRow key={i.key} i={i} {...p} child />)}
    </>
  );
}

function SectionRows({ by, items, ...p }: RowProps & { by: GroupBy; items: Incident[] }) {
  const { t } = useI18n();
  const keyOf = (i: Incident) => by === "olt" ? i.target.oltName || "—" : by === "pon" ? `${i.target.oltName} · PON ${i.target.ponPath}` : title(i.kind, i.severity, t);
  const sections = new Map<string, Incident[]>();
  for (const i of items) sections.set(keyOf(i), [...(sections.get(keyOf(i)) ?? []), i]);
  return (
    <>
      {[...sections.entries()].map(([k, xs]) => (
        <SectionBlock key={k} label={k} xs={xs} {...p} />
      ))}
    </>
  );
}

function SectionBlock({ label, xs, ...p }: RowProps & { label: string; xs: Incident[] }) {
  return (
    <>
      <tr className="border-b border-rule/60 bg-white/[0.02]"><td colSpan={9} className="px-4 py-1.5 text-[11px] font-semibold tracking-[0.12em] text-slate-400 uppercase">{label} <span className="data ml-1 text-slate-500">{xs.length}</span></td></tr>
      {xs.map((i) => <IncidentRow key={i.key} i={i} {...p} />)}
    </>
  );
}

/** Raw view: one row per alert, for when the correlation must be checked. */
function RawRows({ alerts, incidents, match, now, onOpen }: { alerts: Alert[]; incidents: Incident[]; match: (i: Incident) => boolean; now: number; onOpen: (k: string) => void }) {
  const { t, locale } = useI18n();
  const keep = new Set(incidents.filter(match).map((i) => i.key));
  const rows = alerts.filter((a) => keep.has(`${a.target_type}:${a.target_id}`)).sort((a, b) => Date.parse(b.opened_at) - Date.parse(a.opened_at));
  return (
    <>
      {rows.map((a) => (
        <tr key={a.id} onClick={() => onOpen(`${a.target_type}:${a.target_id}`)} className="cursor-pointer border-b border-rule/50 hover:bg-white/[0.025]">
          <td />
          <td className="px-3 py-1.5"><SevTag s={(a.severity === "critical" ? "critical" : a.severity === "warn" ? "warn" : "info")} /></td>
          <td className="px-3 py-1.5"><div className="truncate text-slate-200">{title(kindOf(a), a.severity === "critical" ? "critical" : "warn", t)}</div><div className="data truncate text-[11px] text-slate-500">{a.metric}</div></td>
          <td className="truncate px-3 py-1.5 text-slate-300">{a.target_label}<div className="data text-[11px] text-slate-500">{a.target_place}</div></td>
          <td className="data px-3 py-1.5 text-slate-300">{fmtVal(a)}</td>
          <td className="data px-3 py-1.5 text-slate-400" title={fmtDateTime(locale, a.opened_at)}>{durText(now - Date.parse(a.opened_at))}</td>
          <td className="px-3 py-1.5 text-[11px] text-slate-400 uppercase">{t(a.status === "ack" ? "al.state.ack" : "al.state.unack")}</td>
          <td className="truncate px-3 py-1.5 text-[12px] text-slate-500">{a.assignee_email?.split("@")[0] ?? ""}</td>
          <td className="data px-3 py-1.5 text-right text-[11px] text-slate-600">#{a.id}</td>
        </tr>
      ))}
    </>
  );
}

function RowMenu({ i, canAct, onOpen }: { i: Incident; canAct: boolean; onOpen: () => void }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { bulk } = useAlertActions();
  const ids = i.alerts.map((a) => a.id);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => { if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("mousedown", close); window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", close); };
  }, [open]);
  const item = (label: string, onClick: () => void, icon?: ReactNode) => (
    <button type="button" role="menuitem" onClick={() => { setOpen(false); onClick(); }}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-slate-200 hover:bg-white/[0.05]">{icon}{label}</button>
  );
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={t("al.actions")} onClick={() => setOpen(!open)}
        className="grid size-6 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100"><FiMoreHorizontal /></button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-52 overflow-hidden rounded-md border border-rule bg-[#070b14] py-1 text-left shadow-2xl">
          {item(t("al.act.details"), onOpen)}
          {i.target.ontId !== null && <Link role="menuitem" to={`/onts/${i.target.ontId}`} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] text-slate-200 hover:bg-white/[0.05]"><FiExternalLink className="size-3.5" />{t("al.act.ont")}</Link>}
          {i.target.oltId !== null && i.target.ponPath && <Link role="menuitem" to={`/olts/${i.target.oltId}?pon=${encodeURIComponent(i.target.ponPath)}`} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] text-slate-200 hover:bg-white/[0.05]"><FiExternalLink className="size-3.5" />{t("al.act.pon")}</Link>}
          {canAct && (
            <>
              <div className="my-1 border-t border-rule" />
              {i.owner !== user?.email && item(t("al.act.assignMe"), () => bulk.mutate({ ids, action: "assign", user_id: user!.id }), <FiUser className="size-3.5" />)}
              {i.owner && item(t("al.act.unassign"), () => bulk.mutate({ ids, action: "assign", user_id: null }))}
              {i.suppressed
                ? item(t("al.act.unmute"), () => bulk.mutate({ ids, action: "suppress", minutes: 0 }))
                : [60, 240, 1440].map((m) => <div key={m}>{item(t("al.act.mute", { d: durText(m * 60_000) }), () => bulk.mutate({ ids, action: "suppress", minutes: m }), <FiBellOff className="size-3.5" />)}</div>)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- acknowledge ----------

function AckDialog({ incidents, onClose }: { incidents: Incident[]; onClose: () => void }) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const { bulk } = useAlertActions();
  const ids = incidents.flatMap((i) => i.alerts.map((a) => a.id));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("al.ackTitle", { n: incidents.length })}</DialogTitle>
          <DialogDescription>{t("al.ackMeaning")}</DialogDescription>
        </DialogHeader>
        <ul className="max-h-40 space-y-0.5 overflow-y-auto text-[12.5px] text-slate-300">
          {incidents.slice(0, 12).map((i) => <li key={i.key} className="truncate">· {title(i.kind, i.severity, t)} — {i.target.label}</li>)}
          {incidents.length > 12 && <li className="text-slate-500">{t("al.more", { n: incidents.length - 12 })}</li>}
        </ul>
        <label className="block space-y-1">
          <span className="text-xs text-slate-400">{t("al.note")}</span>
          <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder={t("al.notePh")} autoFocus />
        </label>
        {bulk.error && <p className="text-[12.5px] text-signal-crit">{(bulk.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={bulk.isPending} onClick={() => bulk.mutate({ ids, action: "ack", note }, { onSuccess: onClose })}><FiCheck /> {t("al.act.ack")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- drawer ----------

type AlertEvent = { id: number; event: string; detail: string | null; at: string; actor_email: string };

function IncidentDrawer({ i, group, now, canAct, onClose, onAck }: { i: Incident; group: Group | null; now: number; canAct: boolean; onClose: () => void; onAck: (x: Incident[]) => void }) {
  const { t, locale } = useI18n();
  const [closing, setClosing] = useState(false);
  const [tech, setTech] = useState(false);
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && !closing && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose, closing]);
  const evq = useQueries({ queries: i.alerts.map((a) => ({ queryKey: ["alert-events", a.id], queryFn: () => api<{ events: AlertEvent[] }>(`/api/v1/alerts/${a.id}/events`) })) });
  const timeline = evq.flatMap((q, k) => (q.data?.events ?? []).map((e) => ({ ...e, alert: i.alerts[k] }))).sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const optical = i.alerts.some((a) => a.metric === "ont.rx_power_dbm" || a.metric === "ont.olt_rx_dbm");
  const Icon = KIND_ICON[i.kind];
  const a = i.primary;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
      <aside role="dialog" aria-modal aria-label={title(i.kind, i.severity, t)} onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[600px] flex-col overflow-y-auto border-l border-rule bg-[#070b14]">
        <header className="border-b border-rule px-5 py-4">
          <div className="flex items-start gap-3">
            <SevTag s={i.severity} big />
            <div className="min-w-0 flex-1">
              <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-[0.04em] text-slate-100 uppercase"><Icon />{title(i.kind, i.severity, t)}</h2>
              <p className="mt-0.5 text-[13.5px] text-slate-200">{i.target.label}</p>
              <p className="data text-[12px] text-slate-500">{i.target.place}</p>
            </div>
            <button type="button" onClick={onClose} aria-label={t("common.close")} className="p-1 text-slate-400 hover:text-slate-100"><FiX className="size-4" /></button>
          </div>
          {group && <p className="mt-2 rounded-sm border border-signal-crit/30 bg-signal-crit/[0.06] px-2 py-1 text-[12px] text-rose-100">{t("al.partOf", { what: t(`al.grp.${group.kind}` as Key, { pon: group.ponPath, olt: group.oltName }), n: group.children.length })}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {canAct && !i.acked && <Button size="sm" onClick={() => onAck([i])}><FiCheck /> {t("al.act.ack")}</Button>}
            {i.target.ontId !== null && <Link to={`/onts/${i.target.ontId}`} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-rule px-3 text-[12.5px] text-slate-200 hover:border-signal/50"><FiExternalLink /> {t("al.act.ont")}</Link>}
            {i.target.oltId !== null && i.target.ponPath && <Link to={`/olts/${i.target.oltId}?pon=${encodeURIComponent(i.target.ponPath)}`} className="inline-flex h-8 items-center rounded-md border border-rule px-3 text-[12.5px] text-slate-200 hover:border-signal/50">{t("al.act.pon")}</Link>}
            {i.target.oltId !== null && <Link to={`/olts/${i.target.oltId}`} className="inline-flex h-8 items-center rounded-md border border-rule px-3 text-[12.5px] text-slate-200 hover:border-signal/50">{t("al.act.olt")}</Link>}
          </div>
        </header>
        <div className="space-y-4 px-5 py-4 text-[13px]">
          {isDown(i.kind) && (
            <Sec title={t("al.d.cause")}>
              <p className="text-slate-200">{t(`al.cause.${i.kind}` as Key)}</p>
              {a.run_state === "Online" && <p className="mt-1 text-[12px] text-signal-good">{t("al.backOnlineNote")}</p>}
            </Sec>
          )}
          {!isDown(i.kind) && (
            <Sec title={t("al.d.signal")}>
              {perMetric(i.alerts).map((x) => (
                <KV key={x.id} k={t(`al.metric.${x.metric.replace(/\./g, "_")}` as Key)}>
                  <span className={cn("data", i.stale ? "text-slate-400" : x.severity === "critical" ? "text-signal-crit" : "text-signal-warn")}>{fmtVal(x)}</span>
                  <span className="text-slate-500"> · {thresholdText(x, t)}</span>
                  {x.value_at && <span className={cn("block text-[11.5px]", now - Date.parse(x.value_at) > STALE_MS ? "text-amber-200" : "text-slate-500")}>{t("al.measured", { age: durText(now - Date.parse(x.value_at)) })}</span>}
                </KV>
              ))}
              {i.stale && <p className="text-[12px] text-amber-200">{t("al.staleNote")}</p>}
            </Sec>
          )}
          {optical && i.target.ontId !== null && <SignalHistory ontId={i.target.ontId} />}
          <Sec title={t("al.d.status")}>
            <KV k={t("al.col.age")}><span className="data">{durText(now - Date.parse(i.openedAt))}</span> <span className="text-slate-500">· {fmtDateTime(locale, i.openedAt)}</span></KV>
            <KV k={t("al.col.state")}>{i.acked ? t("al.ackedBy", { who: i.ackedBy || "—" }) : t("al.notAcked")}</KV>
            {i.alerts.find((x) => x.ack_note) && <KV k={t("al.note")}>{i.alerts.find((x) => x.ack_note)!.ack_note}</KV>}
            <KV k={t("al.col.owner")}>{i.owner || t("al.unassigned")}</KV>
            {i.suppressed && <KV k={t("al.state.muted")}>{t("al.mutedUntil", { at: fmtDateTime(locale, i.alerts.find((x) => x.suppressed_until)!.suppressed_until!) })}</KV>}
          </Sec>
          <Sec title={t("al.d.timeline")}>
            {timeline.length === 0 && <p className="text-slate-500">{t("common.loading")}</p>}
            <ol className="space-y-1.5">
              {timeline.map((e) => (
                <li key={e.id} className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 text-[12.5px]">
                  <span className="data text-slate-500" title={fmtDateTime(locale, e.at)}>{shortTime(locale, e.at)}</span>
                  <span className="text-slate-300">{t(`al.ev.${e.event}` as Key, { who: e.actor_email || t("al.system"), what: title(kindOf(e.alert), e.alert.severity === "critical" ? "critical" : "warn", t) })}
                    {e.detail && e.event !== "opened" && <span className="text-slate-500"> — {e.event === "suppressed" ? fmtDateTime(locale, e.detail) : e.detail}</span>}</span>
                </li>
              ))}
            </ol>
          </Sec>
          <section>
            <button type="button" onClick={() => setTech(!tech)} className="eyebrow flex items-center gap-1 hover:text-slate-200">{tech ? <FiChevronDown /> : <FiChevronRight />}{t("al.d.tech")}</button>
            {tech && (
              <div className="mt-1.5 space-y-1">
                {i.alerts.map((x) => (
                  <div key={x.id} className="data rounded-sm border border-rule px-2 py-1.5 text-[11.5px] text-slate-400">
                    #{x.id} · {x.metric} · {x.severity}{x.condition ? ` · ${x.condition} ${x.threshold}` : ""} · {x.rule_id ? t("al.src.rule") : t("al.src.olt")}
                    {x.message && <div className="text-slate-500">{x.message}</div>}
                  </div>
                ))}
                <p className="text-[11.5px] text-slate-500">{t("al.tech.serial", { s: a.serial ?? "—" })}</p>
              </div>
            )}
          </section>
          {canAct && (
            <section className="border-t border-rule pt-3">
              <button type="button" onClick={() => setClosing(true)} className="text-[12px] text-slate-500 hover:text-rose-300">{t("al.act.closeRecord")}</button>
            </section>
          )}
        </div>
        {closing && <CloseDialog i={i} onClose={() => setClosing(false)} onDone={onClose} />}
      </aside>
    </div>
  );
}

function CloseDialog({ i, onClose, onDone }: { i: Incident; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const { close } = useAlertActions();
  const active = !(isDown(i.kind) && i.primary.run_state === "Online");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("al.closeTitle")}</DialogTitle>
          <DialogDescription>{i.target.label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-[13px] text-slate-300">
          <p>{t("al.closeBody")}</p>
          {active && <p className="text-amber-200">{t(isDown(i.kind) ? "al.closeWarnDown" : "al.closeWarnTelemetry")}</p>}
          <p className="text-[12px] text-slate-500">{t("al.closeAlt")}</p>
        </div>
        {close.error && <p className="text-[12.5px] text-signal-crit">{(close.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="destructive" disabled={close.isPending} onClick={() => close.mutate(i.alerts.map((a) => a.id), { onSuccess: () => { onClose(); onDone(); } })}>{t("al.act.closeRecord")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignalHistory({ ontId }: { ontId: number }) {
  const { t, locale } = useI18n();
  const bands = useSignalBands();
  const qs = useQueries({ queries: ["ont.rx_power_dbm", "ont.olt_rx_dbm"].map((m) => ({
    queryKey: ["ont-history", ontId, m, "24h"],
    queryFn: () => api<ONTHistoryResponse>(`/api/v1/onts/${ontId}/history?metric=${m}&window=24h`),
  })) });
  const series = qs.map((q) => q.data?.points ?? []);
  const all = series.flat();
  if (qs.some((q) => q.isLoading)) return <Sec title={t("al.d.history")}><p className="text-slate-500">{t("common.loading")}</p></Sec>;
  if (!all.length) return <Sec title={t("al.d.history")}><p className="text-slate-500">{t("al.noHistory")}</p></Sec>;
  const vals = [...all.map((p) => p.value), bands.warn, bands.crit];
  const lo = Math.floor(Math.min(...vals) - 1), hi = Math.ceil(Math.max(...vals) + 1);
  const t0 = Math.min(...all.map((p) => Date.parse(p.ts))), t1 = Math.max(...all.map((p) => Date.parse(p.ts)));
  const x = (ts: string) => ((Date.parse(ts) - t0) / Math.max(1, t1 - t0)) * 100;
  const y = (v: number) => ((hi - v) / (hi - lo)) * 100;
  const COL = ["#22d3ee", "#a78bfa"];
  return (
    <Sec title={t("al.d.history")}>
      <div className="relative h-40 rounded-sm border border-rule bg-panel p-2">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
          {[{ v: bands.warn, c: "#f59e0b" }, { v: bands.crit, c: "#ef4444" }].map((b) => (
            <line key={b.v} x1="0" x2="100" y1={y(b.v)} y2={y(b.v)} stroke={b.c} strokeDasharray="1.5 1.5" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
          ))}
          {series.map((pts, k) => pts.length > 1 && (
            <polyline key={k} fill="none" stroke={COL[k]} strokeWidth="1.5" vectorEffect="non-scaling-stroke"
              points={pts.map((p) => `${x(p.ts)},${y(p.value)}`).join(" ")} />
          ))}
        </svg>
        <span className="data absolute top-1 left-2 text-[10px] text-slate-500">{hi} dBm</span>
        <span className="data absolute bottom-1 left-2 text-[10px] text-slate-500">{lo} dBm</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 text-[11px] text-slate-400">
        <span><span style={{ color: COL[0] }}>—</span> {t("al.metric.ont_rx_power_dbm")}</span>
        <span><span style={{ color: COL[1] }}>—</span> {t("al.metric.ont_olt_rx_dbm")}</span>
        <span className="text-signal-warn">- - {t("al.sev.warn")} {bands.warn}</span>
        <span className="text-signal-crit">- - {t("al.sev.critical")} {bands.crit}</span>
        <span className="ml-auto data text-slate-500">{fmtDateTime(locale, new Date(t0).toISOString()).replace(/:\d\d$/, "")} → {t("al.now")}</span>
      </div>
    </Sec>
  );
}

/** One reading per metric: warning and critical on the same signal are one line, at the worse severity. */
function perMetric(alerts: Alert[]): Alert[] {
  const by = new Map<string, Alert>();
  for (const a of alerts) {
    const cur = by.get(a.metric);
    if (!cur || (a.severity === "critical" && cur.severity !== "critical")) by.set(a.metric, a);
  }
  return [...by.values()];
}

const shortTime = (locale: string, iso: string) =>
  new Date(iso).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const Sec = ({ title, children }: { title: string; children: ReactNode }) => (
  <section><h3 className="eyebrow mb-1.5">{title}</h3><div className="space-y-1">{children}</div></section>
);
const KV = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-2 border-b border-rule/50 pb-1 text-[12.5px] last:border-b-0">
    <span className="text-slate-500">{k}</span><span className="min-w-0 text-slate-200">{children}</span>
  </div>
);

// ---------- resolved ----------

function ResolvedView() {
  const { t, locale } = useI18n();
  const now = useNow(60_000);
  const [days, setDays] = useState(7);
  const since = useMemo(() => new Date(Date.now() - days * 86_400_000).toISOString(), [days]);
  const q = useQuery({ queryKey: ["alerts", "resolved", days], queryFn: () => api<AlertsResponse>(`/api/v1/alerts?view=resolved&since=${encodeURIComponent(since)}&limit=500`) });
  const rows = q.data?.alerts ?? [];
  const auto = rows.filter((a) => !a.closed_by_email).length;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-400">
        {[1, 7, 30].map((d) => (
          <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)}
            className={cn("rounded-sm border px-2 py-1 text-[12px]", days === d ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
            {t("al.lastDays", { n: d })}
          </button>
        ))}
        <span className="data ml-2">{t("al.res.sum", { n: fmtNum(locale, rows.length), auto: fmtNum(locale, auto) })}</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
        <table className="w-full min-w-[1000px] table-fixed text-[13px]">
          <thead><tr className="border-b border-rule text-left">
            {(["al.col.sev", "al.col.incident", "al.col.target", "al.res.opened", "al.res.duration", "al.res.how"] as Key[]).map((k, i) => (
              <th key={k} className={cn("eyebrow px-3 py-2 font-medium", ["w-[5.2rem]", "w-[22%]", "w-[28%]", "w-[14%]", "w-[9%]", ""][i])}>{t(k)}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((a) => {
              const k = kindOf(a);
              const dur = a.closed_at ? Date.parse(a.closed_at) - Date.parse(a.opened_at) : null;
              return (
                <tr key={a.id} className="border-b border-rule/50 last:border-b-0">
                  <td className="px-3 py-1.5"><SevTag s={a.severity === "critical" ? "critical" : a.severity === "warn" ? "warn" : "info"} /></td>
                  <td className="truncate px-3 py-1.5 text-slate-200">{title(k, a.severity === "critical" ? "critical" : "warn", t)}</td>
                  <td className="px-3 py-1.5"><div className="truncate text-slate-300">{a.target_label}</div><div className="data truncate text-[11px] text-slate-500">{a.target_place}</div></td>
                  <td className="data px-3 py-1.5 text-[12px] text-slate-400" title={fmtDateTime(locale, a.opened_at)}>{t("al.ago", { age: durText(now - Date.parse(a.opened_at)) })}</td>
                  <td className="data px-3 py-1.5 text-slate-300">{durText(dur)}</td>
                  <td className="px-3 py-1.5 text-[12px]">
                    {a.closed_by_email ? <span className="text-slate-400">{t("al.res.closedBy", { who: a.closed_by_email })}</span>
                      : <span className="text-signal-good">{t("al.res.auto")}</span>}
                    {a.acked_by_email && <span className="text-slate-500"> · {t("al.res.ackedBy", { who: a.acked_by_email })}</span>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !q.isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">{t("al.res.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11.5px] text-slate-500"><FiClock className="mr-1 inline" />{t("al.res.note")}</p>
    </div>
  );
}
